import { getKmfcClient } from '../../lib/supabase';

const SHOP_STATUS_EVENT = 'shop-status-changed';

const formatShopStatusLabel = (isOpen) => (isOpen ? 'Restaurant is geopend' : 'Restaurant is gesloten');

const fetchShopStatusFallback = async (kmfcClient) => {
  try {
    const { data: status, error: statusError } = await kmfcClient
      .from('shop_settings')
      .select('*')
      .maybeSingle();

    if (statusError) {
      console.error('[shop-status] Status query error:', statusError);
      return { isOpen: false, source: 'error' };
    }

    const webvonkVersion = status?.Mijn_webvonk_website_versie ?? null;

    if (status?.is_closed === true) {
      return { isOpen: false, source: 'manual_close', webvonkVersion };
    }

    const now = new Date();
    const currentDay = now.getDay() === 0 ? 7 : now.getDay();
    const currentTime = now.toTimeString().slice(0, 5);

    const { data: hours, error: hoursError } = await kmfcClient
      .from('opening_hours')
      .select('*')
      .eq('weekday', currentDay)
      .eq('is_active', true)
      .maybeSingle();

    if (hoursError || !hours) {
      return { isOpen: false, source: 'no_hours' };
    }

    const isOpen = currentTime >= hours.open_time && currentTime <= hours.close_time;

    return {
      currentDay,
      currentTime,
      openTime: hours.open_time,
      closeTime: hours.close_time,
      isOpen,
      source: 'opening_hours',
      webvonkVersion,
    };
  } catch (error) {
    console.error('[shop-status] Fallback error:', error);
    return { isOpen: false, source: 'error' };
  }
};

const fetchShopStatus = async () => {
  const kmfcClient = getKmfcClient();
  if (!kmfcClient) {
    throw new Error('KMFC client not available');
  }

  try {
    // RPC functie bestaat niet, gebruik fallback
    return fetchShopStatusFallback(kmfcClient);
  } catch (error) {
    console.error('[shop-status] Fetch error:', error);
    return fetchShopStatusFallback(kmfcClient);
  }
};

const dispatchShopStatusEvent = (isOpen) => {
  window.dispatchEvent(new CustomEvent(SHOP_STATUS_EVENT, { detail: { isOpen } }));
};

const shouldShowClosedWarning = async () => {
  const kmfcClient = getKmfcClient();
  if (!kmfcClient) return false;

  try {
    const now = new Date();
    const currentDay = now.getDay() === 0 ? 7 : now.getDay();
    const currentTime = now.toTimeString().slice(0, 5);

    // Check of het NIET dinsdag is
    if (currentDay === 2) { // 2 = dinsdag
      return false;
    }
    
    // Check of het tussen 12:00 en 22:00 is
    const currentHour = parseInt(currentTime.split(':')[0]);
    const currentMinute = parseInt(currentTime.split(':')[1]);
    const currentTotalMinutes = currentHour * 60 + currentMinute;
    const startMinutes = 12 * 60; // 12:00
    const endMinutes = 22 * 60; // 22:00
    
    
    if (currentTotalMinutes < startMinutes || currentTotalMinutes >= endMinutes) {
      return false; // Buiten 12:00-22:00
    }
    
    // Haal openingstijden op
    const { data: hours, error: hoursError } = await kmfcClient
      .from('opening_hours')
      .select('*')
      .eq('weekday', currentDay)
      .eq('is_active', true)
      .maybeSingle();

    if (hoursError || !hours) {
      return false; // Geen openingstijden gevonden
    }
    
    
    // Controleer of site volgens openingstijden open zou moeten zijn
    const shouldBeOpen = currentTime >= hours.open_time && currentTime <= hours.close_time;
    
    // Basis openingstijden zijn 12:00-22:00
    const baseOpenTime = '12:00';
    const baseCloseTime = '22:00';
    const isBaseHours = hours.open_time === baseOpenTime && hours.close_time === baseCloseTime;
    
    // Als site volgens openingstijden gesloten is EN het basis 12-22 is, DAN is het een fout
    if (!shouldBeOpen && isBaseHours) {
      return true;
    }
    
    // Als site volgens openingstijden open is, controleer shop_status
    const { data: status, error: statusError } = await kmfcClient
      .from('shop_settings')
      .select('*')
      .maybeSingle();
    
    
    if (statusError || !status) {
      return false; // Geen status gevonden
    }
    
    // Als site handmatig gesloten is met notitie, geen popup
    if (status.is_closed && status.exception_note && status.exception_note.trim() !== '') {
      return false;
    }
    
    // Als site handmatig gesloten is zonder notitie, toon popup
    if (status.is_closed) {
      return true;
    }
    
    return false;
    
  } catch (error) {
    console.error('[shop-status] Error checking closed warning:', error);
    return false;
  }
};

export {
  SHOP_STATUS_EVENT,
  fetchShopStatus,
  dispatchShopStatusEvent,
  formatShopStatusLabel,
  shouldShowClosedWarning,
};
